// 假資料產生器（§16.1）。
//
// 硬性規則（§16.1 / TEST-DATA-AND-PII-POLICY §3.2）：
//   - 全部匿名、固定、可重複。
//   - 使用有意義的繁體中文，不用 lorem ipsum / foo / bar。
//   - 不從原系統複製任何真實值。
//   - 保留原系統的欄位型態與格式（日期 yyyy/MM/dd、編號有前綴）。
//   - 產出必須通過 src/test-support/pii-guard.ts（fixtures.test.ts 實跑驗證）。
//
// **確定性**：完全不用 Math.random() / Date.now()。每一列的值由
// mulberry32(hashSeed(`${resource}#${index}`)) 決定，所以：
//   - 同一個 resource 的第 i 列永遠一模一樣，跟總筆數、呼叫順序都無關；
//   - 不同 resource 的資料互不相同（seed 不同）。
//
// 欄位來源（2026-09-01 起）：
//   1. 原系統實測表頭 —— .tmp/ui/original/**/dom.json 的 tableHeaders，143 頁裡 79 頁有表格。
//      抽成 legacy-table-headers.generated.ts，中文標籤 → FieldKind 的推斷規則在 page-columns.ts。
//   2. 其餘 64 頁沒有表格可抄，仍退回下面四種 PageShape 通用欄位。
//
// ponytail: 「等欄位在 Gate 4 定案再加專屬 fixture」這件事現在成立了，但仍**沒有**為 79 頁
// 各寫一份 fixture 檔——只多了一張表頭資料表與一張 12 條的推斷規則表，值的產生器完全沒有分家。

import { moduleLabels, type PageMeta, type PageShape, pageCatalog } from '@/mocks/page-catalog';
import {
  legacyColumnsFor,
  STATUS_ENABLED,
  STATUS_PROGRESS,
  STATUS_REVIEW,
} from '@/mocks/page-columns';
import type { Row } from '@/mocks/types';

// ---------------------------------------------------------------------------
// 確定性亂數
// ---------------------------------------------------------------------------

/** FNV-1a 32-bit：把 resource 字串轉成 seed。純函式，跨平台結果一致。 */
export function hashSeed(input: string): number {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

/**
 * mulberry32：32-bit seeded PRNG，回傳 [0, 1) 的數。
 * 自己寫（12 行）而不是裝 seedrandom 之類的套件——§本次不新增任何 npm 依賴。
 */
export function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function pick<T>(pool: readonly T[], rand: () => number): T {
  const item = pool[Math.floor(rand() * pool.length)];
  if (item === undefined) {
    throw new Error('fixture pool 不得為空');
  }
  return item;
}

// ---------------------------------------------------------------------------
// 中文詞彙池
// ---------------------------------------------------------------------------
//
// 全部是虛構、通用的教學醫院詞彙，沒有一個來自原系統資料。
//
// 「人員」刻意不用姓名（連「陳姓學員」這種寫法都不用）：pii-guard 的姓名規則會攔下
// 「常見姓氏字開頭、且獨立成段的 2~4 個漢字」，「陳姓學員」正好命中。這裡改用
// 「學員甲」「住院醫師乙」這種代號式稱謂，既是有意義的繁體中文，也不需要靠 allowlist
// 放行——fixture 本來就不該出現任何看起來像姓名的字串。

/** 主題名稱。刻意都是 5 個字以上，讀起來像原系統的表單／課程名稱。 */
const TOPICS: readonly string[] = [
  '臨床技能評核表',
  '病歷寫作評量表',
  '教學門診記錄表',
  '跨領域團隊照護研討',
  '實證醫學讀書會',
  '急救訓練課程',
  '感染管制講習',
  '醫學倫理研討會',
  '臨床技能中心實作',
  '住院醫師甄選作業',
  '學習護照認證項目',
  '期中口試評分表',
  '迷你臨床演練評量',
  '操作型技術評量表',
  '教學計畫審查作業',
  '師資培育工作坊',
];

/** 單位。虛構的教學醫院部門名稱。 */
const UNITS: readonly string[] = [
  '內科部',
  '外科部',
  '婦產部',
  '小兒部',
  '急診醫學部',
  '麻醉部',
  '放射線部',
  '護理部',
  '教學部',
  '家庭醫學部',
];

/** 人員代號式稱謂（見上方說明：不使用任何姓名樣式的字串）。 */
const PEOPLE: readonly string[] = [
  '學員甲',
  '學員乙',
  '學員丙',
  '學員丁',
  '學員戊',
  '學員己',
  '教師甲',
  '教師乙',
  '教師丙',
  '住院醫師甲',
  '住院醫師乙',
  '實習醫學生甲',
  '護理師甲',
];

const CATEGORIES: readonly string[] = [
  '必修',
  '選修',
  '共同課程',
  '專科訓練',
  '院內講習',
  '線上課程',
  '實地操作',
  '個案討論',
];

// STATUS_ENABLED／STATUS_PROGRESS／STATUS_REVIEW 三個狀態值池搬到 page-columns.ts，
// 因為「哪一種狀態欄配哪一個池」已經是那邊推斷規則（R03a/R03b/R03c）的一部分。

// ---------------------------------------------------------------------------
// 欄位與形狀
// ---------------------------------------------------------------------------

export type FieldKind =
  | 'id'
  | 'title'
  | 'category'
  | 'unit'
  | 'person'
  | 'date'
  | 'endDate'
  | 'score'
  | 'status'
  | 'text'
  | 'module';

export interface Column {
  readonly key: string;
  /** 欄位標題。原系統固定文字定案後應改由 messages 提供，這裡先放預設中文。 */
  readonly label: string;
  readonly kind: FieldKind;
  /** kind === 'status' 時的可選值。 */
  readonly pool?: readonly string[];
}

const SHAPES: Readonly<Record<PageShape, readonly Column[]>> = {
  list: [
    { key: 'id', label: '編號', kind: 'id' },
    { key: 'title', label: '名稱', kind: 'title' },
    { key: 'category', label: '類別', kind: 'category' },
    { key: 'unit', label: '單位', kind: 'unit' },
    { key: 'status', label: '狀態', kind: 'status', pool: STATUS_ENABLED },
    { key: 'createdBy', label: '建立者', kind: 'person' },
    { key: 'createdAt', label: '建立日期', kind: 'date' },
  ],
  report: [
    { key: 'id', label: '編號', kind: 'id' },
    { key: 'title', label: '項目名稱', kind: 'title' },
    { key: 'unit', label: '單位', kind: 'unit' },
    { key: 'student', label: '學員', kind: 'person' },
    { key: 'score', label: '分數', kind: 'score' },
    { key: 'completedAt', label: '完成日期', kind: 'date' },
    { key: 'status', label: '狀態', kind: 'status', pool: STATUS_PROGRESS },
  ],
  form: [
    { key: 'id', label: '編號', kind: 'id' },
    { key: 'title', label: '表單名稱', kind: 'title' },
    { key: 'category', label: '類別', kind: 'category' },
    { key: 'unit', label: '單位', kind: 'unit' },
    { key: 'startDate', label: '開始日期', kind: 'date' },
    { key: 'endDate', label: '結束日期', kind: 'endDate' },
    { key: 'status', label: '狀態', kind: 'status', pool: STATUS_REVIEW },
    { key: 'owner', label: '承辦人員', kind: 'person' },
    { key: 'description', label: '說明', kind: 'text' },
  ],
  display: [
    { key: 'id', label: '編號', kind: 'id' },
    { key: 'title', label: '項目', kind: 'title' },
    { key: 'source', label: '來源模組', kind: 'module' },
    { key: 'content', label: '內容', kind: 'text' },
    { key: 'updatedAt', label: '更新日期', kind: 'date' },
  ],
};

const FALLBACK_META: PageMeta = { module: 'unresolved', shape: 'list' };

/** resource（＝ legacy-candidates.json 的 pageId）對應的盤點中繼資料。 */
export function resourceMeta(resource: string): PageMeta {
  return pageCatalog[resource] ?? FALLBACK_META;
}

/**
 * 該 resource 的欄位定義。頁面可以直接拿去畫表頭，不用另外維護一份。
 *
 * 先查原系統實測到的逐頁表頭（79 頁），查不到才退回 PageShape 的四種通用欄位。
 */
export function columnsFor(resource: string): readonly Column[] {
  return legacyColumnsFor(resource) ?? SHAPES[resourceMeta(resource).shape];
}

/** 該 resource 所屬模組的中文名稱。 */
export function moduleLabelFor(resource: string): string {
  return moduleLabels[resourceMeta(resource).module] ?? '待決候選清單';
}

// ---------------------------------------------------------------------------
// 值的產生
// ---------------------------------------------------------------------------

/** 編號前綴：模組 slug 去掉連字號後的前三個字母大寫（announcement → ANN）。 */
function idPrefix(module: string): string {
  const letters = module.replace(/-/g, '').slice(0, 3).toUpperCase();
  return letters === '' ? 'GEN' : letters;
}

/**
 * 編號格式：`<前綴>-<民國年>-<四碼流水號>`，例如 `ANN-114-0001`。
 * 保留原系統「有前綴的編號」格式；刻意不用「英文字母 + 9 碼數字」這種
 * 會被 pii-guard 判成身分證字號的排列。
 */
export function buildId(resource: string, index: number): string {
  return `${idPrefix(resourceMeta(resource).module)}-114-${String(index + 1).padStart(4, '0')}`;
}

/** 固定基準日：2026/01/05（UTC）。不使用 Date.now()。 */
const DATE_EPOCH_MS = Date.UTC(2026, 0, 5);
const DAY_MS = 86400000;

/** 日期格式 yyyy/MM/dd —— 原系統格式，不改成 ISO。 */
function formatDate(offsetDays: number): string {
  const date = new Date(DATE_EPOCH_MS + offsetDays * DAY_MS);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
}

/** 在詞彙池上做確定性位移，供同一列出現多個同 kind 欄位時取到不同的值。 */
function rotate<T>(pool: readonly T[], base: number, step: number): T {
  const item = pool[(base + step) % pool.length];
  if (item === undefined) {
    throw new Error('fixture pool 不得為空');
  }
  return item;
}

/**
 * 產生第 index 列（0-based）。同樣的 (resource, index) 永遠得到同樣的列。
 *
 * 逐頁欄位之後，同一列可能出現多個同 kind 的欄位（例如
 * report-tsgh-plan-form-static 的「平均／最低／最高／中位數／眾數」全是 score）。
 * 第 n 個同 kind 欄位用 `step = n` 在同一組已抽好的值上做**確定性位移**，
 * 而不是多抽亂數——這樣：
 *   - step 0 的結果與改版前逐字元相同（四種 PageShape 每種 kind 都只有一欄），
 *   - 亂數消耗量不受欄位數影響，`mulberry32(hashSeed(resource#index))` 的性質原封不動。
 */
export function buildRow(resource: string, index: number): Row {
  return buildValues(resource, index, columnsFor(resource));
}

/**
 * 依一組欄位定義產生一列值。`buildRow` 用 `columnsFor(resource)` 呼叫它，
 * 表單頁（DisplayPage 的原系統主表單）用 `legacyFormColumnsFor(pageId)` 呼叫它。
 *
 * 抽成獨立函式是為了**不動 `columnsFor`**：`columnsFor` 同時決定清單頁的表頭，
 * 動它會連帶改到 143 頁裡每一個吃 CrudListPage／ReportPage 的頁面。
 * 亂數種子仍然只由 (resource, index) 決定，與欄位清單無關，所以
 * `buildRow` 的輸出逐字元不變。
 */
export function buildValues(
  resource: string,
  index: number,
  columns: readonly Column[],
): Row {
  const meta = resourceMeta(resource);
  const rand = mulberry32(hashSeed(`${resource}#${index}`));

  // 先抽好共用的語意值，讓「說明」欄可以引用同一列的主題與單位，讀起來像真的資料。
  // 抽的是「池中的位置」而不是值本身，才有辦法在同 kind 多欄時往後位移；
  // 每個 rand() 的呼叫次數與順序都跟原本的 pick() 完全一樣。
  const topicIndex = Math.floor(rand() * TOPICS.length);
  const unitIndex = Math.floor(rand() * UNITS.length);
  const personIndex = Math.floor(rand() * PEOPLE.length);
  const categoryIndex = Math.floor(rand() * CATEGORIES.length);
  const startOffset = Math.floor(rand() * 300);
  const score = 60 + Math.floor(rand() * 41);

  const row: Row = {};
  // 逐頁欄位可能整頁都沒有 id 欄（例如公告清單），但 MockDataSource 的
  // get／update／delete 與 data-ui-id 的列鍵都靠 row.id 認人，所以固定先給一個，
  // 再讓欄位定義覆寫。四種 PageShape 本來就都有 id 欄，對它們沒有任何差別。
  row.id = buildId(resource, index);

  const used = new Map<FieldKind, number>();
  for (const column of columns) {
    const step = used.get(column.kind) ?? 0;
    used.set(column.kind, step + 1);
    switch (column.kind) {
      case 'id':
        // 第二個以後的編號欄（員編 vs 學號）換一段流水號，才不會整列印出同一個字串。
        row[column.key] = buildId(resource, index + step * 1000);
        break;
      case 'title':
        row[column.key] = `${rotate(TOPICS, topicIndex, step)}（第 ${String(index + 1)} 版）`;
        break;
      case 'category':
        row[column.key] = rotate(CATEGORIES, categoryIndex, step);
        break;
      case 'unit':
        row[column.key] = rotate(UNITS, unitIndex, step);
        break;
      case 'person':
        row[column.key] = rotate(PEOPLE, personIndex, step);
        break;
      case 'date':
        row[column.key] = formatDate(startOffset + step * 7);
        break;
      case 'endDate':
        row[column.key] = formatDate(startOffset + 14 + step * 7);
        break;
      case 'score':
        row[column.key] = 60 + ((score - 60 + step * 7) % 41);
        break;
      case 'status':
        row[column.key] = pick(column.pool ?? STATUS_ENABLED, rand);
        break;
      case 'module':
        row[column.key] = moduleLabels[meta.module] ?? '待決候選清單';
        break;
      case 'text':
        row[column.key] =
          `${rotate(TOPICS, topicIndex, step)}，適用於${rotate(UNITS, unitIndex, step)}，` +
          `由${rotate(PEOPLE, personIndex, step)}維護。`;
        break;
    }
  }
  return row;
}

/** 產生 count 列。rows[i] 與 count 無關，所以 12 筆與 137 筆的前 12 列完全相同。 */
export function buildRows(resource: string, count: number): Row[] {
  const rows: Row[] = [];
  for (let index = 0; index < count; index += 1) {
    rows.push(buildRow(resource, index));
  }
  return rows;
}
